import { useEffect, useRef, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import AuthImage from "../components/AuthImage";
import UserAvatar from "../components/UserAvatar";
import RichText from "../components/RichText";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getSocket } from "../lib/socket";
import { toast } from "sonner";
import { Search, Send, Check, CheckCheck, Edit2, Paperclip, X, Mic, Square } from "lucide-react";

export default function ChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // peer user
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [pendingMedia, setPendingMedia] = useState([]); // [{file_id,url,content_type,kind}]
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [search, setSearch] = useState("");
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [searchResults, setSearchResults] = useState([]);
  const [typingPeer, setTypingPeer] = useState(false);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);
  const socketRef = useRef(null);

  // Initial conversations
  const loadConvs = async () => {
    const { data } = await api.get(`/chat/conversations`);
    setConversations(data.items || []);
  };
  useEffect(() => { loadConvs(); }, []);

  // Search users
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get(`/chat/users/search`, { params: { q: search } });
      setSearchResults(data.items || []);
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  // Open a conversation
  const open = async (peer) => {
    setActive(peer);
    const { data } = await api.get(`/chat/conversations/${peer.id}/messages`);
    setMessages(data.items || []);
    await api.post(`/chat/conversations/${peer.id}/read`).catch(() => {});
    await loadConvs();
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    // Tell server we joined this room
    socketRef.current?.emit("join_conversation", { conversation_id: data.conversation_id });
  };

  // Socket setup
  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    socketRef.current = s;
    const onMsg = (m) => {
      // If it's for the active conversation, append
      if (active && (m.from_user_id === active.id || m.to_user_id === active.id)) {
        setMessages((arr) => [...arr.filter((x) => x.id !== m.id), m]);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        if (m.to_user_id === user?.id) api.post(`/chat/conversations/${active.id}/read`).catch(() => {});
      }
      loadConvs();
    };
    const onEdit = (m) => setMessages((arr) => arr.map((x) => (x.id === m.id ? m : x)));
    const onRead = (d) => {
      if (active && d.reader_user_id === active.id) {
        setMessages((arr) => arr.map((m) => (m.from_user_id === user?.id && !m.read_at ? { ...m, read_at: d.at } : m)));
      }
    };
    const onTyping = (d) => {
      if (active && d.user_id === active.id) {
        setTypingPeer(d.is_typing);
        clearTimeout(typingTimer.current);
        if (d.is_typing) typingTimer.current = setTimeout(() => setTypingPeer(false), 3000);
      }
    };
    s.on("message_sent", onMsg);
    s.on("message_edited", onEdit);
    s.on("message_read", onRead);
    s.on("user_typing", onTyping);
    return () => {
      s.off("message_sent", onMsg);
      s.off("message_edited", onEdit);
      s.off("message_read", onRead);
      s.off("user_typing", onTyping);
    };
  }, [active, user?.id]);

  const send = async () => {
    if (!active || (!text.trim() && pendingMedia.length === 0)) return;
    const optimistic = {
      id: `tmp-${Date.now()}`,
      from_user_id: user.id, to_user_id: active.id,
      from_name: user.full_name, from_handle: user.handle, from_avatar: user.avatar_url,
      content: text, media: pendingMedia, created_at: new Date().toISOString(), sending: true,
    };
    setMessages((m) => [...m, optimistic]);
    const sentText = text; const sentMedia = pendingMedia;
    setText(""); setPendingMedia([]);
    try {
      const { data } = await api.post(`/chat/messages`, { to_user_id: active.id, content: sentText, media: sentMedia });
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? data : x)));
    } catch (e) {
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? { ...x, error: true } : x)));
      toast.error(formatApiError(e));
    }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Pre-validate every file BEFORE we send anything — fail fast with a
    // friendly error rather than letting the ingress reject with a 413.
    for (const f of files) {
      const isVideo = f.type.startsWith("video/");
      const kind = isVideo ? "post_video" : "post_photo";
      const sizeErr = validateUpload(kind, f);
      if (sizeErr) {
        toast.error(sizeErr);
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const isVideo = f.type.startsWith("video/");
        const kind = isVideo ? "post_video" : "post_photo";
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post(`/uploads/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ file_id: data.file_id, url: data.url, content_type: data.content_type, kind: isVideo ? "video" : "image" });
      }
      setPendingMedia((cur) => [...cur, ...uploaded]);
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePending = (i) => setPendingMedia((cur) => cur.filter((_, idx) => idx !== i));

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Voice messages aren't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 200) { toast("Recording too short."); return; }
        setUploading(true);
        try {
          const fd = new FormData(); fd.append("file", blob, "voice-note.webm");
          const { data } = await api.post(`/uploads/voice_note`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setPendingMedia((cur) => [...cur, { file_id: data.file_id, url: data.url, content_type: data.content_type, kind: "audio" }]);
          toast.success("Voice note ready — hit Send.");
        } catch (err) { toast.error(formatApiError(err) || "Voice upload failed"); }
        finally { setUploading(false); }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const emitTyping = (val) => {
    if (!active) return;
    const conv_id = [user.id, active.id].sort().join(":");
    socketRef.current?.emit("typing", { conversation_id: conv_id, is_typing: val });
  };

  const editMsg = async (m) => {
    const next = window.prompt("Edit message:", m.content);
    if (next == null || next.trim() === m.content) return;
    try { await api.patch(`/chat/messages/${m.id}`, { content: next.trim() }); }
    catch (e) { window.alert(e?.response?.data?.detail || "Failed to edit."); }
  };

  const canEdit = (m) =>
    m.from_user_id === user?.id &&
    m.created_at &&
    (Date.now() - new Date(m.created_at).getTime() < 15 * 60 * 1000);

  return (
    <DashboardShell>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 h-[calc(100vh-220px)]" data-testid="chat-page">
        <aside className="csc-card overflow-hidden flex flex-col">
          <div className="p-3 border-b border-[rgba(0,170,255,0.18)] flex items-center gap-2">
            <Search size={14} className="text-[#94a3b8]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find or start a conversation…" className="csc-input !py-2" data-testid="chat-search" />
          </div>
          <div className="overflow-auto flex-1">
            {searchResults.length > 0 && (
              <>
                <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8] px-3 pt-2">New chat</div>
                {searchResults.map((u) => (
                  <button key={u.id} onClick={() => { open(u); setSearch(""); }} className="w-full flex items-center gap-3 p-3 hover:bg-[#00aaff]/5 border-b border-[rgba(0,170,255,0.18)]" data-testid={`chat-start-${u.id}`}>
                    <UserAvatar user={u} size="md" data-testid={`chat-start-avatar-${u.id}`} />
                    <div className="text-left min-w-0">
                      <div className="text-sm text-white truncate">{u.name}</div>
                      <div className="font-mono-tech text-[10px] text-[#94a3b8]">@{u.handle}</div>
                    </div>
                  </button>
                ))}
                <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8] px-3 pt-3">Conversations</div>
              </>
            )}
            {conversations.length === 0 && searchResults.length === 0 && (
              <div className="p-8 text-center text-[#94a3b8] text-xs font-mono-tech uppercase tracking-wider">No conversations yet. Search above to start one.</div>
            )}
            {conversations.map((c) => (
              <button key={c.conversation_id} onClick={() => open(c.peer)} className={`w-full flex items-center gap-3 p-3 hover:bg-[#00aaff]/5 border-b border-[rgba(0,170,255,0.18)] ${active?.id === c.peer.id ? "bg-[#00aaff]/10" : ""}`} data-testid={`chat-conv-${c.peer.id}`}>
                <UserAvatar user={c.peer} size="md" data-testid={`chat-conv-avatar-${c.peer.id}`} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white truncate">{c.peer.name}</span>
                    <span className="font-mono-tech text-[10px] text-[#94a3b8]">{c.last_message?.created_at ? new Date(c.last_message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono-tech text-[11px] text-[#94a3b8] truncate">{c.last_message?.content || (c.last_message?.media?.length ? "📎 Media" : "")}</span>
                    {c.unread > 0 && <span className="bg-[#39ff14] text-[#050d1a] text-[10px] font-bold rounded-full px-1.5">{c.unread}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="csc-card flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[#94a3b8] text-sm">Select a conversation to start chatting.</div>
          ) : (
            <>
              <div className="p-3 border-b border-[rgba(0,170,255,0.18)] flex items-center gap-3">
                <UserAvatar user={active} size="md" data-testid={`chat-header-avatar-${active.id}`} />
                <div className="min-w-0">
                  <div className="text-sm text-white">{active.name}</div>
                  <div className="font-mono-tech text-[10px] text-[#94a3b8]">@{active.handle}{typingPeer && <span className="ml-2 text-[#39ff14]">… typing</span>}</div>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="chat-thread">
                {messages.map((m) => {
                  const mine = m.from_user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[70%] rounded-md px-3 py-2 ${mine ? "bg-[#00aaff]/20 border border-[#00aaff]/40" : "bg-[#0d1b2e] border border-[rgba(0,170,255,0.18)]"}`} style={mine ? { boxShadow: "0 0 10px rgba(0, 170, 255,0.18)" } : {}}>
                        {(m.media || []).length > 0 && (
                          <div className="space-y-2 mb-1" data-testid={`msg-media-${m.id}`}>
                            {(m.media || []).map((md, i) => {
                              const mKey = md.file_id || `${m.id}-${md.url || i}`;
                              return (md.kind === "audio" || (md.content_type || "").startsWith("audio/"))
                                ? <audio key={mKey} controls className="w-full max-w-xs" src={`${md.url}?auth=${localStorage.getItem("auth_token") || ""}`} data-testid={`msg-audio-${m.id}-${i}`} />
                                : md.kind === "video" || (md.content_type || "").startsWith("video/")
                                ? <video key={mKey} controls className="w-full rounded border border-[rgba(0,170,255,0.18)] max-h-80" src={`${md.url}?auth=${localStorage.getItem("auth_token") || ""}`} data-testid={`msg-video-${m.id}-${i}`} />
                                : <AuthImage key={mKey} src={md.url} className="w-full rounded border border-[rgba(0,170,255,0.18)] max-h-80 object-contain" data-testid={`msg-image-${m.id}-${i}`} />;
                            })}
                          </div>
                        )}
                        {m.content && <div className="text-sm whitespace-pre-wrap csc-break-anywhere"><RichText text={m.content} /></div>}
                        <div className="mt-1 flex items-center gap-1 font-mono-tech text-[10px] text-[#94a3b8] justify-end">
                          {m.edited && <span className="italic">edited</span>}
                          <span>{m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          {mine && (m.read_at ? <CheckCheck size={11} className="text-[#39ff14]" /> : m.sending ? <span>…</span> : <Check size={11} />)}
                          {mine && canEdit(m) && <button onClick={() => editMsg(m)} className="ml-1 hover:text-[#00aaff]" data-testid={`edit-msg-${m.id}`}><Edit2 size={10} /></button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-[rgba(0,170,255,0.18)] space-y-2">
                {pendingMedia.length > 0 && (
                  <div className="flex gap-2 flex-wrap" data-testid="chat-pending-media">
                    {pendingMedia.map((md, i) => (
                      <div key={md.file_id || `pending-${i}`} className="relative csc-card !p-1.5 flex items-center gap-2 text-[10px] font-mono-tech">
                        <span className="text-[#00aaff]">{md.kind === "video" ? "🎬" : "🖼️"} {md.content_type}</span>
                        <button onClick={() => removePending(i)} className="text-red-400 hover:text-red-300" data-testid={`pending-media-remove-${i}`}><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={onPickFiles} className="hidden" data-testid="chat-file-input" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading || recording} className="csc-btn-ghost !p-2" title="Attach media" data-testid="chat-attach">
                    <Paperclip size={14} />
                  </button>
                  {!recording ? (
                    <button onClick={startRecording} disabled={uploading} className="csc-btn-ghost !p-2" title="Record voice note" data-testid="chat-mic">
                      <Mic size={14} />
                    </button>
                  ) : (
                    <button onClick={stopRecording} className="csc-btn-secondary !p-2" title="Stop recording" data-testid="chat-mic-stop">
                      <Square size={14} />
                    </button>
                  )}
                  <input
                    value={text}
                    onChange={(e) => { setText(e.target.value); emitTyping(true); clearTimeout(typingTimer.current); typingTimer.current = setTimeout(() => emitTyping(false), 1200); }}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                    placeholder={recording ? "Recording…" : uploading ? "Uploading…" : "Type a message…"}
                    className="csc-input flex-1"
                    disabled={uploading || recording}
                    data-testid="chat-input"
                  />
                  <button onClick={send} disabled={(!text.trim() && pendingMedia.length === 0) || recording} className="csc-btn-primary" data-testid="chat-send"><Send size={14} /></button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}
